import { ROLE_TYPES, type RoleType } from '@scio3/shared-types';
import { usePermissions } from '@/hooks/usePermissions';

export type TagEntity = 'screen' | 'asset' | 'playlist';

/**
 * Frontend mirror of the backend tag-management gate (tag.service.ts
 * canManageTags): isRootSuperAdmin bypass, then the existing per-entity
 * `<entity>CanTag` role permission (permission.service.ts ROLE_VALUE_TYPES).
 * The backend enforces the real gate on every query/mutation — this hook only
 * drives nav/tab VISIBILITY, so it must agree with the backend or users see
 * tabs that 403 (or miss tabs they may use).
 *
 * Built-in role defaults mirror ROLE_VALUE_TYPES exactly: ADMIN / TEAM_OWNER /
 * USER / CONTENT_APPROVAL carry CanTag=true; CONTENT_PROPOSAL / READ_ONLY
 * false. (CONTENT_APPROVAL=true diverges from the design sketch's table — the
 * backend values are the enforcement truth for the reused permission.)
 * Custom roles read their stored `<entity>CanTag` flags.
 *
 * MUTATIONS are gated harder (backend assertCanAdministerTags): sweeps rewrite
 * entities team-wide and cannot honor folder security, so create/rename/delete
 * require root Owner/SuperAdmin/Admin or team ADMIN/TEAM_OWNER. That predicate
 * subsumes the per-type read gate (bypass / all-true), so it collapses to
 * `isRootSuperAdmin || isTeamAdmin` — exposed as `canAdministerTags` and used
 * for the Tags-manager nav entries (a mutation surface).
 */
export function useCanManageTags() {
  const { rootRole, teamRole, isTeamAdmin, currentTeam } = usePermissions();
  const isRootSuperAdmin = rootRole === ROLE_TYPES.OWNER || rootRole === ROLE_TYPES.SUPER_ADMIN;

  function canManageTags(entity: TagEntity): boolean {
    if (isRootSuperAdmin) return true; // backend: isRootSuperAdmin(rootRole) bypass
    if (!teamRole) return false; // auth not loaded — deny until known
    if (isTeamAdmin) return true; // ADMIN / TEAM_OWNER: ROLE_VALUE_TYPES all-true

    const perms = currentTeam?.permissions as Record<string, boolean> | undefined;
    // Custom role (ID > 1 char) without permissions = deleted role, deny
    if (!perms && (teamRole as string).length > 1) return false;
    if (perms) return perms[`${entity}CanTag`] ?? false;

    switch (teamRole as RoleType) {
      case ROLE_TYPES.USER:
      case ROLE_TYPES.CONTENT_APPROVAL:
        return true;
      case ROLE_TYPES.CONTENT_PROPOSAL:
      case ROLE_TYPES.READ_ONLY:
        return false;
      default:
        return false;
    }
  }

  const canScreen = canManageTags('screen');
  const canAsset = canManageTags('asset');
  const canPlaylist = canManageTags('playlist');

  return {
    canManageTags,
    canScreen,
    canAsset,
    canPlaylist,
    /** any universe readable (tag pickers, list queries) */
    canManageAnyTags: canScreen || canAsset || canPlaylist,
    /** mutation gate — mirrors backend assertCanAdministerTags; drives the Tags-manager nav entries */
    canAdministerTags: isRootSuperAdmin || isTeamAdmin,
  };
}
